import { useState } from "react";
import { cs } from "@/i18n/cs";
import { useJarvisChat } from "@/jarvis/useJarvisChat";

/**
 * Talk to Jarvis (spec §66/§74, /Design Mobile): velký orb tap-to-talk,
 * mikrofon jen po klepnutí; bez voice klíčů textový vstup.
 */
export function MobileJarvisPage() {
  const jarvis = useJarvisChat();
  const [input, setInput] = useState("");
  const reply = jarvis.exchange?.reply ?? null;

  const send = (text: string) => {
    if (!text.trim()) return;
    setInput("");
    jarvis.send(text);
  };

  const stateLine = jarvis.recording
    ? cs.mobile.jarvisListening
    : jarvis.pending
      ? cs.mobile.jarvisThinking
      : cs.mobile.jarvisTapIdle;

  return (
    <div className="af-animate-fade flex flex-col items-center px-1 pt-2" data-testid="mobile-jarvis">
      <div
        className="af-animate-pulse mb-5 text-[11px] tracking-[0.22em]"
        style={{ color: jarvis.recording || jarvis.pending ? "var(--accent)" : "var(--text-secondary)" }}
      >
        {stateLine}
      </div>

      {/* velký orb — tap = hlas (pokud je), jinak fokus na input */}
      <button
        type="button"
        className="relative mb-5 h-[180px] w-[180px] cursor-pointer"
        onClick={() => {
          if (jarvis.voiceAvailable) void jarvis.toggleRecording();
          else document.getElementById("mobile-jarvis-input")?.focus();
        }}
        data-testid="mobile-orb"
      >
        <div
          className="af-orb-core absolute rounded-full"
          style={{
            inset: 22,
            background: "radial-gradient(circle at 34% 28%, #c9f7ff 0%, #4fdcf2 22%, #0e7f96 55%, #032b34 85%)",
          }}
        />
        <svg width="180" height="180" viewBox="0 0 190 190" className="af-orb-ring absolute inset-0">
          <ellipse cx="95" cy="95" rx="90" ry="32" fill="none" stroke="color-mix(in srgb, var(--accent) 40%, transparent)" strokeWidth="1" strokeDasharray="7 11" />
        </svg>
        <svg width="180" height="180" viewBox="0 0 190 190" className="af-orb-ring-r absolute inset-0">
          <ellipse cx="95" cy="95" rx="32" ry="90" fill="none" stroke="color-mix(in srgb, var(--accent) 25%, transparent)" strokeWidth="1" strokeDasharray="5 13" />
        </svg>
        {(jarvis.recording || jarvis.pending) && (
          <div className="absolute -bottom-2 left-1/2 flex h-4 -translate-x-1/2 items-end gap-[3px]">
            {[10, 17, 7, 14, 10].map((h, i) => (
              <div key={i} className="af-orb-bar w-[3px]" style={{ height: h, background: "var(--accent)", animationDelay: `${i * 0.1}s` }} />
            ))}
          </div>
        )}
      </button>

      {!jarvis.exchange ? (
        <>
          <div className="mb-4 whitespace-pre-line text-center text-[12px] leading-relaxed" style={{ color: "var(--text-secondary)" }}>
            {jarvis.online
              ? jarvis.voiceAvailable
                ? cs.mobile.jarvisTapNote
                : cs.mobile.jarvisTypeNote
              : cs.jarvis.offlineIntro}
          </div>
          {jarvis.online ? (
            <div className="flex w-full flex-col gap-1.5">
              {cs.jarvis.suggestions.slice(0, 3).map((q) => (
                <button
                  key={q}
                  type="button"
                  className="cursor-pointer px-3.5 py-2.5 text-center text-[12px]"
                  style={{ borderRadius: "var(--radius-box)", border: "1px solid color-mix(in srgb, var(--accent) 20%, transparent)", color: "var(--accent)" }}
                  onClick={() => send(q)}
                  data-testid="mobile-jarvis-suggestion"
                >
                  „{q}"
                </button>
              ))}
            </div>
          ) : null}
        </>
      ) : (
        <div className="af-animate-fade w-full p-3.5" style={{ borderRadius: "var(--radius-box)", border: "1px solid color-mix(in srgb, var(--accent) 25%, transparent)", background: "var(--surface-overlay)" }}>
          <div className="mb-2 text-[12px]" style={{ color: "var(--text-primary)" }}>
            „{jarvis.exchange.user}"
          </div>
          <div
            className="mb-2.5 pl-2.5 text-[12px] leading-[1.65]"
            style={{ borderLeft: "2px solid color-mix(in srgb, var(--accent) 35%, transparent)", color: "var(--text-primary)" }}
            data-testid="mobile-jarvis-reply"
          >
            {reply ? reply.reply : <span className="af-animate-pulse">{cs.jarvis.thinking}</span>}
          </div>
          {jarvis.speechBlocked ? (
            <button
              type="button"
              className="mb-2.5 cursor-pointer px-3 py-2 text-[11px] tracking-[0.1em]"
              style={{ borderRadius: "var(--radius-box)", color: "var(--accent)", border: "1px solid color-mix(in srgb, var(--accent) 40%, transparent)" }}
              title={cs.jarvis.playBlockedNote}
              onClick={jarvis.playBlockedSpeech}
              data-testid="mobile-jarvis-play-blocked"
            >
              {cs.jarvis.playBlocked}
            </button>
          ) : null}
          {reply && reply.impact.length > 0 ? (
            <div className="mb-2.5 px-2.5 py-2" style={{ borderRadius: "var(--radius-box)", border: "1px solid color-mix(in srgb, var(--positive) 25%, transparent)", background: "color-mix(in srgb, var(--positive) 3%, transparent)" }}>
              {reply.impact.map((line, i) => (
                <div key={i} className="py-0.5 text-[11px]" style={{ color: "var(--text-primary)" }}>
                  <span style={{ color: "var(--positive)" }}>→</span> {line}
                </div>
              ))}
            </div>
          ) : null}
          <div className="flex flex-wrap items-center justify-between gap-2">
            {reply?.chip ? (
              <span
                className="px-2.5 py-1 text-[11px] tracking-[0.1em]"
                style={{ borderRadius: "var(--radius-box)",
                  color: reply.pendingAction ? "var(--critical)" : "var(--accent)",
                  border: `1px solid ${reply.pendingAction ? "color-mix(in srgb, var(--critical) 50%, transparent)" : "color-mix(in srgb, var(--accent) 30%, transparent)"}`,
                }}
              >
                {reply.chip}
              </span>
            ) : (
              <span />
            )}
            {reply?.pendingAction ? (
              <button
                type="button"
                className="cursor-pointer px-3 py-2 text-[11px] font-bold"
                style={{ background: "var(--critical)", color: "var(--text-on-accent)" }}
                disabled={jarvis.confirmPending}
                onClick={() => jarvis.confirm(reply.pendingAction!)}
                data-testid="mobile-jarvis-confirm"
              >
                {reply.pendingAction.confirmLabel}
              </button>
            ) : null}
          </div>
        </div>
      )}

      {jarvis.error ? (
        <div className="mt-2 text-[11px]" style={{ color: "var(--critical)" }}>
          ⚠ {jarvis.error}
        </div>
      ) : null}

      {jarvis.online ? (
        <div className="mt-3.5 flex w-full items-center gap-2 px-1">
          <input
            id="mobile-jarvis-input"
            className="flex-1 bg-transparent py-2 font-mono text-[11px] outline-none"
            style={{ borderBottom: "1px solid color-mix(in srgb, var(--accent) 20%, transparent)", color: "var(--text-primary)" }}
            placeholder={cs.jarvis.placeholder}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") send(input);
            }}
            data-testid="mobile-jarvis-input"
          />
          {jarvis.voiceAvailable ? (
            <button
              type="button"
              className="cursor-pointer px-2.5 py-1.5 text-[11px]"
              style={{ borderRadius: "var(--radius-box)",
                color: jarvis.voiceOutput ? "var(--accent)" : "var(--text-secondary)",
                border: `1px solid ${jarvis.voiceOutput ? "color-mix(in srgb, var(--accent) 50%, transparent)" : "var(--border-default)"}`,
              }}
              title={jarvis.voiceOutput ? cs.jarvis.voiceOutputOn : cs.jarvis.voiceOutputOff}
              onClick={() => jarvis.setVoiceOutput(!jarvis.voiceOutput)}
              data-testid="mobile-jarvis-voice-output"
            >
              {jarvis.voiceOutput ? "🔊" : "🔇"}
            </button>
          ) : null}
          <button
            type="button"
            className="cursor-pointer px-3 py-1.5 text-[11px]"
            style={{ borderRadius: "var(--radius-box)", color: "var(--accent)", border: "1px solid color-mix(in srgb, var(--accent) 30%, transparent)" }}
            onClick={() => send(input)}
            disabled={jarvis.pending}
            data-testid="mobile-jarvis-send"
          >
            →
          </button>
        </div>
      ) : null}
    </div>
  );
}
